// MARK://dhk8/pos/features/kds/useKdsRealtime
// Bridges @dockethub/realtime → react-query for the KDS board. When a <RealtimeProvider>
// is mounted (and the dhk8 WS gateway is live), this subscribes to the tenant/branch
// ticket channel and invalidates the active-tickets query whenever a
// `dhk8.kds.ticket.*` event arrives. When no provider/gateway is present the hook is
// a no-op and the board falls back to polling (useActiveTickets, POLL_MS).
//
// Channel naming: el gateway ya aterrizó. pos-realtime autoriza y publica en
// `t:{tenantId}:kds` (ver services/pos-realtime/src/channels.ts → kdsChannel);
// el board re-filtra por estación en el cliente.

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRealtimeContext, useRealtimeSubscription, type RealtimeStatus } from '@dockethub/realtime'
import { useTenantId } from '../../hooks/useTenantId.js'
import { ticketKeys } from './useTickets.js'

/** Canal KDS tenant-scoped del server dhk8-pos-realtime: `t:{tid}:kds`
 *  (tenant-wide; el board re-filtra por estación en el cliente). Cadena vacía
 *  si no hay tenant → `useRealtimeSubscription` no-op. */
export function kdsTicketChannel(tenantId: string | null): string {
  return tenantId !== null && tenantId.length > 0 ? `t:${tenantId}:kds` : ''
}

/** @returns the realtime status ('idle' when no provider is mounted). */
export function useKdsRealtime(stationId: string | null): RealtimeStatus {
  const qc = useQueryClient()
  const tenantId = useTenantId()
  const { client, status } = useRealtimeContext()
  // El board re-filtra por estación en el cliente; el canal es tenant-wide.
  void stationId
  const channel = kdsTicketChannel(tenantId)

  const onTicketEvent = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ticketKeys.all })
  }, [qc])

  // useRealtimeSubscription is a no-op when there's no client in context.
  useRealtimeSubscription(channel, onTicketEvent, [tenantId])

  return client ? status : 'idle'
}
